import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Subsidiary, { CreateSubsidiaryRequest, UpdateSubsidiaryRequest } from "../models/Subsidiary";
import { ApiErrorResponse } from "../models/ApiResponse";
import { apiStore } from "../stores/api.store";
import { toast } from "../utils/toast";

const KEY = "subsidiaries";

export function useSubsidiaries(enabled = true) {
	const { data, ...rest } = useQuery(
		[KEY],
		() =>
			apiStore
				.get<{ totalCount: number; subsidiaries: Subsidiary[] }>({
					path: "/subsidiary",
					isAuthRequired: true,
					query: { pageSize: 1000 },
				})
				.then((d) => d.data),
		{ enabled, staleTime: 5 * 60 * 1000 }
	);

	return {
		subsidiaries: data?.subsidiaries ?? [],
		totalCount: data?.totalCount ?? 0,
		...rest,
	};
}

export function useSubsidiary(subsidiaryId?: string, enabled = true) {
	const { data, ...rest } = useQuery(
		[KEY, subsidiaryId],
		() =>
			apiStore
				.get<Subsidiary>({
					path: `/subsidiary/${subsidiaryId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ enabled: enabled && !!subsidiaryId }
	);

	return { subsidiary: data, ...rest };
}

export function useCreateSubsidiary() {
	const queryClient = useQueryClient();

	return useMutation<Subsidiary, ApiErrorResponse, CreateSubsidiaryRequest>(
		(body) =>
			apiStore
				.post<Subsidiary>({
					path: "/subsidiary",
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				toast.successGeneric();
				queryClient.invalidateQueries([KEY]);
			},
			onError: () => toast.errorGeneric(),
		}
	);
}

export function useUpdateSubsidiary() {
	const queryClient = useQueryClient();

	return useMutation<
		Subsidiary,
		ApiErrorResponse,
		{ subsidiaryId: string; update: UpdateSubsidiaryRequest }
	>(
		({ subsidiaryId, update }) =>
			apiStore
				.put<Subsidiary>({
					path: `/subsidiary/${subsidiaryId}`,
					body: update,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				toast.successGeneric();
				queryClient.invalidateQueries([KEY]);
			},
			onError: () => toast.errorGeneric(),
		}
	);
}

export function useDeleteSubsidiary() {
	const queryClient = useQueryClient();

	return useMutation<unknown, ApiErrorResponse, string>(
		(subsidiaryId) =>
			apiStore
				.delete({
					path: `/subsidiary/${subsidiaryId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries([KEY]);
			},
			// Deletion is blocked while consultants/customers reference it — the
			// caller surfaces the specific message via its own onError.
		}
	);
}
